import { booleanParam, numberParam, stringParam } from '../types/checks.js';
import type { CheckResult, ValidationCheck } from '../types/checks.js';
import { roundTo } from '../types/geometry.js';
import { analyseHeadwear } from '../face/headwear.js';
import { missingRequirementReason, notChecked, plainResult } from './support.js';

/**
 * Headwear check.
 *
 * The core reports visual symptoms only; it never states the document's rule.
 * When the profile marks headwear as allowed for religious or medical reasons,
 * the check degenerates to pass / warning and the message says so explicitly.
 */
export const headwearCheck: ValidationCheck = {
  id: 'headwear',
  title: 'Головной убор',
  description:
    'Ищет крупную гладкую область над линией роста волос и нетипично ровный силуэт причёски.',
  kind: 'content',
  requires: ['pixels', 'geometry'],

  run: (ctx): CheckResult => {
    const missing = missingRequirementReason(ctx, ['pixels', 'geometry']);
    if (missing) return notChecked(ctx, headwearCheck, missing, ctx.rule.severity);
    const geometry = ctx.geometry;
    const pixels = ctx.pixels;
    if (!geometry || !pixels) {
      return notChecked(
        ctx,
        headwearCheck,
        'Нет данных для анализа области над лбом.',
        ctx.rule.severity,
      );
    }

    const coverageFloor = numberParam(ctx.rule.params, 'minCoverage', 0.45);
    const failCoverage = numberParam(ctx.rule.params, 'failCoverage', 0.6);
    const uncertainBelow = numberParam(ctx.rule.params, 'uncertainBelowConfidence', 0.6);
    const allowedString = stringParam(ctx.rule.params, 'allowed', '');
    const allowedBoolean = booleanParam(ctx.rule.params, 'allowed', false);
    const allowedForReligious = allowedString === 'religious_or_medical' || allowedBoolean;

    const analysis = analyseHeadwear({ pixels, geometry });
    const detected = !analysis.bandTooSmall && analysis.coverage >= coverageFloor && analysis.smooth;
    const confident = analysis.confidence >= uncertainBelow;

    const status: CheckResult['status'] = (() => {
      if (!detected) return 'pass';
      if (allowedForReligious) return 'warning';
      if (analysis.coverage >= failCoverage && confident) return 'fail';
      return confident ? 'warning' : 'uncertain';
    })();

    const message = (() => {
      if (status === 'pass') return '✅ Признаков головного убора не обнаружено';
      if (allowedForReligious) {
        return '⚠ Возможно обнаружен головной убор. Головной убор может быть разрешён по религиозным или медицинским причинам — проверьте требования документа.';
      }
      if (status === 'fail') return '❌ Обнаружен головной убор';
      if (status === 'uncertain') {
        return 'Не удалось надёжно определить, есть ли на голове головной убор.';
      }
      return '⚠ Возможно обнаружен головной убор';
    })();


    return plainResult({
      id: headwearCheck.id,
      title: headwearCheck.title,
      status,
      severity: ctx.rule.severity,
      value: analysis.coverage,
      max: coverageFloor,
      unit: 'ratio',
      ...(ctx.rule.official ? { official: ctx.rule.official } : {}),
      message,
      ...(ctx.rule.official?.statement ? { requirement: ctx.rule.official.statement } : {}),
      fixability: status === 'pass' || allowedForReligious ? 'none' : 'retake',
      geometry: [
        {
          type: 'rect' as const,
          coordinates: [
            roundTo(analysis.bandRect.x, 4),
            roundTo(analysis.bandRect.y, 4),
            roundTo(analysis.bandRect.width, 4),
            roundTo(analysis.bandRect.height, 4),
          ],
        },
      ],
      problemRegions: detected
        ? [
            {
              id: 'headwear_band',
              rect: analysis.bandRect,
              message: 'Возможная область головного убора над линией роста волос.',
              severity: status === 'fail' ? ('error' as const) : ('warning' as const),
            },
          ]
        : [],
      confidence: analysis.confidence,
      details: {
        headwearDetected: detected,
        headwearConfidence: analysis.confidence,
        coverage: analysis.coverage,
        smooth: analysis.smooth,
        flatness: analysis.flatness,
        hairlineSpread: analysis.hairlineSpread,
        bandPixels: analysis.bandPixels,
        foreignPixels: analysis.foreignPixels,
        bandTooSmall: analysis.bandTooSmall,
        minCoverage: coverageFloor,
        failCoverage,
        uncertainBelowConfidence: uncertainBelow,
        allowed: allowedForReligious ? 'religious_or_medical' : 'no',
        mode: analysis.mode,
        notes: analysis.notes,
        displayValue: detected ? 'возможен головной убор' : 'не обнаружен',
      },
    });
  },
};
